/**
 * Recovery Provider Component
 * Manages recovery flow state and provides actions to UI
 */

import { useReducer, useEffect, useRef, useMemo, createContext } from 'react';
import PropTypes from 'prop-types';
import { recoveryReducer } from './reducer';
import { initialState, createAction, RecoveryStep } from './types';
import {
  saveState,
  hydrate,
  cancelPendingWrites,
  markRecoveryCompleted,
  isRecoveryCompleted,
  clearRecoveryCompletion,
  clearState,
  RECOVERY_SKIPPED_KEY,
} from './storage';
import matrixAuthService from '../../services/matrixAuthService';
import recoveryService from '../../services/recoveryService';
import logger from '../../utils/logger';

// Create contexts
export const RecoveryStateContext = createContext(null);
export const RecoveryActionsContext = createContext(null);

// Provider component
export function RecoveryProvider({ children, client }) {
  // Hydrate initial state from storage
  const hydratedState = useMemo(() => hydrate(initialState), []);

  // Initialize reducer with hydrated state
  const [state, dispatch] = useReducer(recoveryReducer, hydratedState);

  // Refs for managing imperative operations
  const inFlightRef = useRef(new Set());
  const abortControllerRef = useRef(null);
  const tempKeyRef = useRef(null);
  const clientRef = useRef(client);
  const mountedRef = useRef(true);

  // Persist state changes to storage
  useEffect(() => {
    saveState(state);
  }, [state]);

  // Handle client changes (login/logout transitions)
  useEffect(() => {
    const previousClient = clientRef.current;

    // Update ref first
    clientRef.current = client;

    // Handle logout (client -> null)
    if (previousClient && !client) {
      logger.debug('RECOVERY: [Provider] Client removed (logout), resetting recovery state');
      dispatch(createAction.reset());
      // Clear recovery completion marker on logout
      clearRecoveryCompletion();
      // Note: Skip flag is cleared centrally in matrixAuthService logout
    }
    // Handle login after logout (null -> client)
    else if (!previousClient && client) {
      logger.debug('RECOVERY: [Provider] New client after logout, resetting recovery state for fresh session');
      dispatch(createAction.reset());
    }
  }, [client]);

  // Initialize recovery state on mount or when client changes
  useEffect(() => {
    if (!client) {
      logger.debug('RECOVERY: [Provider] No client, skipping initialization');
      return;
    }

    // Check if recovery was already completed successfully
    if (state.step === RecoveryStep.DONE && !state.needsRecovery) {
      logger.debug('RECOVERY: [Provider] Recovery already completed successfully, skipping detection');
      return;
    }

    // Check if recovery was completed for this device
    const deviceId = client.getDeviceId();
    if (deviceId && isRecoveryCompleted(deviceId)) {
      logger.debug('RECOVERY: [Provider] Recovery was previously completed for this device:', deviceId);
      // Clear any stale sessionStorage state and reset to clean state
      // We can't call complete() if we're not in VERIFYING step, so just clear the state
      clearState();
      logger.debug('RECOVERY: [Provider] Cleared stale sessionStorage recovery state');
      // If we're in a state that needs recovery but it's already been completed, reset it
      if (state.needsRecovery || state.step !== RecoveryStep.IDLE) {
        dispatch(createAction.reset());
        logger.debug('RECOVERY: [Provider] Reset recovery state since it was already completed');
      }
      return;
    }

    // Check if we need to re-initialize
    // Only initialize if in IDLE or ERROR state
    const shouldInitialize = state.step === RecoveryStep.IDLE ||
                            state.step === RecoveryStep.ERROR;

    logger.debug('RECOVERY: [Provider] Checking if should initialize:', {
      currentStep: state.step,
      shouldInitialize,
      needsRecovery: state.needsRecovery,
    });

    if (!shouldInitialize) {
      logger.debug('RECOVERY: [Provider] Already in recovery flow (step: ' + state.step + '), skipping init');
      return;
    }

    // Check if user has already skipped recovery in this session
    if (sessionStorage.getItem(RECOVERY_SKIPPED_KEY) === 'true') {
      logger.debug('RECOVERY: [Provider] User has already skipped recovery in this session, not showing modal again');
      return;
    }

    const initRecovery = async () => {
      try {
        // Clear the session flag when RecoveryProvider gets client for the first time
        // This ensures recovery detection runs in RecoveryProvider context
        if (window.__recoveryCheckDone) {
          logger.debug('RECOVERY: [Provider] Clearing previous recovery session flag to re-run detection with proper context');
          window.__recoveryCheckDone = false;
        }

        // Note: We no longer reset DONE state here, as that would cause the modal to reappear
        // The DONE state with needsRecovery=false is now preserved

        logger.debug('RECOVERY: [Provider] Detecting recovery requirement');

        // Use matrixAuthService to detect recovery needs
        await matrixAuthService.detectRecoveryRequirement(client);

        // The detectRecoveryRequirement will have called setRecoveryActions
        // which should trigger our init action
      } catch (error) {
        logger.error('[RecoveryProvider] Failed to detect recovery requirement:', error);
        dispatch(createAction.fail({
          code: 'INIT_ERROR',
          message: 'Failed to initialize recovery',
        }));
      }
    };

    initRecovery();
  }, [client, state.step]);

  // Subscribe to recovery events from services
  useEffect(() => {
    const handleRecoveryInit = (data) => {
      // Guard against race condition - don't re-init if user has skipped
      if (sessionStorage.getItem(RECOVERY_SKIPPED_KEY) === 'true') {
        logger.debug('[RecoveryProvider] Recovery init blocked - user has skipped recovery in this session');
        return;
      }
      logger.debug('[RecoveryProvider] Recovery init event:', data);
      dispatch(createAction.init(data));
    };

    const handleProgressUpdate = (progress) => {
      if (!mountedRef.current) return;
      dispatch(createAction.setProgress(progress));
    };

    // Listen for recovery events
    recoveryService.on('progress', handleProgressUpdate);

    // Set recovery actions for matrixAuthService
    matrixAuthService.setRecoveryActions({
      init: handleRecoveryInit,
      reset: () => dispatch(createAction.reset()),
    });

    return () => {
      recoveryService.off('progress', handleProgressUpdate);
      matrixAuthService.setRecoveryActions(null);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      cancelPendingWrites();

      // Abort any in-flight operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Clear temp key
      tempKeyRef.current = null;

      // Clear in-flight tracking
      inFlightRef.current.clear();
    };
  }, []);

  // Action implementations
  const actions = useMemo(() => {
    const openModal = () => {
      if (!state.needsRecovery) {
        logger.warn('[RecoveryProvider] Cannot open modal - recovery not needed');
        return;
      }
      // Provide deterministic startedAt when recovery begins
      dispatch(createAction.openModal(Date.now()));
    };

    const submitKey = async (key) => {
      // Check if already in flight
      if (inFlightRef.current.has('submitKey')) {
        logger.debug('[RecoveryProvider] Submit key already in flight');
        return;
      }

      try {
        inFlightRef.current.add('submitKey');

        // Basic validation
        if (!key || typeof key !== 'string') {
          dispatch(createAction.fail({
            code: 'INVALID_FORMAT',
            message: 'Recovery key is required',
          }));
          return;
        }

        // Validate base58 format (no 0, O, I, l)
        const base58Regex = /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;
        if (!base58Regex.test(key.replace(/\s/g, ''))) {
          dispatch(createAction.fail({
            code: 'INVALID_FORMAT',
            message: 'Recovery key contains invalid characters',
          }));
          return;
        }

        // Store key in memory only (never in state)
        tempKeyRef.current = key;

        // Move to validating
        dispatch(createAction.submitKey(key));

        // Create abort controller for cancellable operations
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        // Start restoration with deterministic startedAt timestamp
        dispatch(createAction.setStep(RecoveryStep.RESTORING, Date.now()));

        const currentClient = clientRef.current;
        if (!currentClient) {
          throw new Error('No Matrix client available');
        }

        // Get backup version first
        const backupInfo = await currentClient.getCrypto()?.getKeyBackupInfo();
        if (!backupInfo || !backupInfo.version) {
          throw new Error('No backup version found');
        }

        // Perform restoration with the recovery key that was already validated
        logger.debug('[RecoveryProvider] Starting key backup restoration');

        // The key is already validated and stored in ephemeral store
        // Just trigger the restoration
        const crypto = currentClient.getCrypto();
        if (crypto && crypto.restoreKeyBackup) {
          await crypto.restoreKeyBackup({
            progressCallback: (progress) => {
              if (!mountedRef.current) return;
              dispatch(createAction.setProgress({
                imported: progress.imported || 0,
                total: progress.total || 0,
              }));
            },
          });
        } else {
          logger.error('[RecoveryProvider] Crypto restore not available');
          throw new Error('Key backup restore not available');
        }

        // Move to SSSS setup and execute it
        await ssss();

      } catch (error) {
        if (error.name === 'AbortError') {
          logger.debug('[RecoveryProvider] Operation aborted');
          dispatch(createAction.setStep(RecoveryStep.AWAIT_KEY));
        } else {
          logger.error('[RecoveryProvider] Submit key failed:', error);
          dispatch(createAction.fail({
            code: error.code || 'RECOVERY_ERROR',
            message: error.message || 'Failed to restore from recovery key',
          }));
        }
      } finally {
        inFlightRef.current.delete('submitKey');
      }
    };

    const restore = async () => {
      // Get the key from ephemeral store (already validated by RecoveryKeyValidator)
      const recoveryEphemeralStore = require('../../services/recoveryEphemeralStore').default;

      // Check ephemeral store first (key was validated and stored there)
      if (recoveryEphemeralStore.hasKey()) {
        logger.debug('[RecoveryProvider] Using validated key from ephemeral store for restore');

        // Move to restoring state
        dispatch(createAction.setStep(RecoveryStep.RESTORING, Date.now()));

        const currentClient = clientRef.current;
        if (!currentClient) {
          dispatch(createAction.fail({
            code: 'NO_CLIENT',
            message: 'No Matrix client available',
          }));
          return;
        }

        try {
          // Get backup info
          const backupInfo = await currentClient.getCrypto()?.getKeyBackupInfo();
          if (!backupInfo || !backupInfo.version) {
            throw new Error('No backup version found');
          }

          logger.debug('[RecoveryProvider] Starting key backup restoration with validated key');

          // The key is already validated and stored in ephemeral store
          // The SDK will call getSecretStorageKey which retrieves it from there
          const crypto = currentClient.getCrypto();
          if (crypto && crypto.restoreKeyBackup) {
            await crypto.restoreKeyBackup({
              progressCallback: (progress) => {
                if (!mountedRef.current) return;
                dispatch(createAction.setProgress({
                  imported: progress.imported || 0,
                  total: progress.total || 0,
                }));
              },
            });
          } else {
            logger.error('[RecoveryProvider] Crypto restore not available');
            throw new Error('Key backup restore not available');
          }

          // Force refresh rooms after recovery to show decrypted messages
          // This ensures messages that were shown as encrypted get re-rendered
          logger.debug('[RecoveryProvider] Triggering room refresh after key restoration');
          setTimeout(() => {
            const matrixService = require('../../services/matrixService').default;
            if (matrixService && matrixService.refreshRooms) {
              matrixService.refreshRooms();
              logger.debug('[RecoveryProvider] Room refresh triggered');
            }
          }, 1000); // Give time for initial decryptions to process

          // Move to SSSS setup and execute it
          await ssss();

        } catch (error) {
          logger.error('[RecoveryProvider] Restore failed:', error);
          dispatch(createAction.fail({
            code: error.code || 'RECOVERY_ERROR',
            message: error.message || 'Failed to restore from recovery key',
          }));
        }
      } else if (tempKeyRef.current) {
        // Fallback to tempKeyRef if available (shouldn't happen in new flow)
        logger.debug('[RecoveryProvider] Using tempKeyRef for restore (legacy path)');
        await submitKey(tempKeyRef.current);
      } else {
        logger.error('[RecoveryProvider] No recovery key available for restore');
        dispatch(createAction.fail({
          code: 'NO_KEY',
          message: 'No recovery key provided',
        }));
      }
    };

    const ssss = async () => {
      const recoveryEphemeralStore = require('../../services/recoveryEphemeralStore').default;

      if (!recoveryEphemeralStore.hasKey() && !tempKeyRef.current) {
        logger.error('[RecoveryProvider] Cannot setup SSSS - missing key');
        return;
      }

      if (!clientRef.current) {
        logger.error('[RecoveryProvider] Cannot setup SSSS - missing client');
        return;
      }

      try {
        // Verify key is available in ephemeral store or tempKeyRef
        if (!recoveryEphemeralStore.hasKey() && !tempKeyRef.current) {
          throw new Error('No recovery key available for SSSS setup');
        }

        // SSSS setup is already done during key restoration
        // Just log and move to device verification
        logger.debug('RECOVERY: [Provider] SSSS already setup during key restoration');

        // Move to device verification and execute it
        await verifyDevice();
      } catch (error) {
        logger.error('[RecoveryProvider] SSSS setup failed:', error);
        dispatch(createAction.fail({
          code: 'SSSS_ERROR',
          message: 'Failed to setup secure storage',
        }));
      }
    };

    const verifyDevice = async () => {
      const recoveryEphemeralStore = require('../../services/recoveryEphemeralStore').default;

      if (!recoveryEphemeralStore.hasKey() && !tempKeyRef.current) {
        logger.error('[RecoveryProvider] Cannot verify device - missing key');
        return;
      }

      if (!clientRef.current) {
        logger.error('[RecoveryProvider] Cannot verify device - missing client');
        return;
      }

      try {
        const crypto = clientRef.current.getCrypto();
        if (!crypto) {
          logger.error('[RecoveryProvider] Cannot verify device - crypto not available');
          dispatch(createAction.fail({
            code: 'CRYPTO_NOT_READY',
            message: 'Crypto not available',
          }));
          return;
        }

        const userId = clientRef.current.getUserId();
        const deviceId = clientRef.current.getDeviceId();

        // Step 1: Bootstrap cross-signing with recovered keys
        // This is CRITICAL - without this, the device won't be verified
        logger.debug('RECOVERY: [Provider] Bootstrapping cross-signing after key restoration');
        try {
          await crypto.bootstrapCrossSigning({
            setupNewCrossSigning: false, // Don't create new keys, use recovered ones
            authUploadDeviceSigningKeys: async (makeRequest) => {
              // Upload device signing keys if needed
              logger.debug('RECOVERY: [Provider] Uploading device signing keys');
              await makeRequest();
            },
          });
          logger.debug('RECOVERY: [Provider] Cross-signing bootstrapped successfully');
        } catch (error) {
          logger.error('[RecoveryProvider] Failed to bootstrap cross-signing:', error);
          // Non-fatal - continue with verification attempt
        }

        // Step 2: Check own cross-signing trust
        // This verifies that the cross-signing keys are properly set up
        logger.debug('RECOVERY: [Provider] Checking own cross-signing trust');
        try {
          const trustStatus = await crypto.checkOwnCrossSigningTrust();
          logger.debug('RECOVERY: [Provider] Cross-signing trust status:', {
            localTrusted: trustStatus?.localTrusted,
            crossSigningVerified: trustStatus?.crossSigningVerified,
          });
        } catch (error) {
          logger.warn('[RecoveryProvider] Could not check cross-signing trust:', error);
          // Non-fatal
        }

        // Step 3: Mark current device as verified
        // This is the final step that makes the session show as "Verified"
        logger.debug('RECOVERY: [Provider] Marking device as verified');
        try {
          // Check current verification status first
          const currentStatus = await crypto.getDeviceVerificationStatus(userId, deviceId);
          logger.debug('RECOVERY: [Provider] Current device verification status:', {
            isVerified: currentStatus?.isVerified,
            signedByOwner: currentStatus?.signedByOwner,
            crossSigningVerified: currentStatus?.crossSigningVerified,
          });

          // Only mark as verified if not already verified
          if (!currentStatus?.isVerified && !currentStatus?.signedByOwner) {
            logger.debug('RECOVERY: [Provider] Device not verified, attempting to mark as verified');
            
            // Try different methods to mark device as verified
            if (crypto.setDeviceVerified) {
              await crypto.setDeviceVerified(userId, deviceId);
              logger.debug('RECOVERY: [Provider] ✅ Device marked as verified via crypto.setDeviceVerified');
            } else {
              logger.warn('[RecoveryProvider] crypto.setDeviceVerified not available');
            }

            // Verify it worked
            const newStatus = await crypto.getDeviceVerificationStatus(userId, deviceId);
            logger.debug('RECOVERY: [Provider] New device verification status:', {
              isVerified: newStatus?.isVerified,
              signedByOwner: newStatus?.signedByOwner,
              crossSigningVerified: newStatus?.crossSigningVerified,
            });

            if (!newStatus?.isVerified && !newStatus?.signedByOwner) {
              logger.warn('[RecoveryProvider] Device still not verified after setDeviceVerified attempt');
            }
          } else {
            logger.debug('RECOVERY: [Provider] Device already verified');
          }
        } catch (error) {
          logger.error('[RecoveryProvider] Failed to verify device:', error);
          // Non-fatal - recovery still succeeded
        }

        // Complete recovery
        logger.debug('RECOVERY: [Provider] Completing recovery, setting needsRecovery to false');
        dispatch(createAction.complete());

        // Mark recovery as completed for this device
        if (deviceId) {
          markRecoveryCompleted(deviceId);
          logger.debug('RECOVERY: [Provider] Marked recovery as completed for device:', deviceId);
        }

        // Clear temp key
        tempKeyRef.current = null;

        // Clear abort controller
        abortControllerRef.current = null;

        // Mark recovery check as done
        window.__recoveryCheckDone = true;
        logger.debug('RECOVERY: [Provider] Recovery completed, window.__recoveryCheckDone set to true');

        // Clear sessionStorage to prevent stale state on next load
        clearState();
        logger.debug('RECOVERY: [Provider] Cleared sessionStorage recovery state after completion');

        // Final room refresh after complete recovery
        // Ensures all messages are properly decrypted and displayed
        setTimeout(() => {
          const matrixService = require('../../services/matrixService').default;
          if (matrixService && matrixService.refreshRooms) {
            matrixService.refreshRooms();
            logger.debug('[RecoveryProvider] Final room refresh after recovery complete');
          }
        }, 500);

      } catch (error) {
        logger.error('[RecoveryProvider] Device verification failed:', error);
        dispatch(createAction.fail({
          code: 'VERIFY_ERROR',
          message: 'Failed to verify device',
        }));
      }
    };

    const complete = () => {
      dispatch(createAction.complete());
      tempKeyRef.current = null;
      abortControllerRef.current = null;
      window.__recoveryCheckDone = true;
      // Clear sessionStorage to prevent stale state on next load
      clearState();
      logger.debug('RECOVERY: [Provider] Cleared sessionStorage recovery state after completion');
    };

    const fail = (error) => {
      const safeError = {
        code: error.code || 'UNKNOWN_ERROR',
        message: error.message || 'An error occurred',
      };
      dispatch(createAction.fail(safeError));
    };

    const reset = () => {
      // Abort any in-flight operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Clear temp key
      tempKeyRef.current = null;

      // Clear in-flight tracking
      inFlightRef.current.clear();

      // Reset state
      dispatch(createAction.reset());
    };

    const skip = () => {
      // Clear transient data but preserve needsRecovery
      tempKeyRef.current = null;
      abortControllerRef.current = null;
      inFlightRef.current.clear();

      // Mark that recovery was skipped in this session
      // This prevents the modal from reappearing immediately
      // Using sessionStorage to persist across page reloads within the same session
      sessionStorage.setItem(RECOVERY_SKIPPED_KEY, 'true');
      logger.debug('RECOVERY: [Provider] Marked recovery as skipped for this session');

      dispatch(createAction.skip());
    };

    return {
      openModal,
      submitKey,
      restore,
      ssss,
      verifyDevice,
      complete,
      fail,
      reset,
      skip,
    };
  }, [state.needsRecovery]);

  // Memoize context values
  const stateValue = useMemo(() => state, [state]);
  const actionsValue = useMemo(() => actions, [actions]);

  return (
    <RecoveryStateContext.Provider value={stateValue}>
      <RecoveryActionsContext.Provider value={actionsValue}>
        {children}
      </RecoveryActionsContext.Provider>
    </RecoveryStateContext.Provider>
  );
}

RecoveryProvider.propTypes = {
  children: PropTypes.node.isRequired,
  client: PropTypes.object, // Matrix client instance
};
